import React, { useEffect, useState } from 'react';
import { api, useServer } from '../contexts/ServerContext';
import { useNavigate } from 'react-router-dom';
import { User, Shield, Clock, Star, MessageCircle, Search } from 'lucide-react';
import { steamId64ToV2, getCachedAvatar, getCachedDisplayName } from '../utils/helpers';

const PAGE_SIZE = 15;

export default function Reports() {
  const { server } = useServer();
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [avatars, setAvatars] = useState({});
  const [names, setNames] = useState({});
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [ratingFilter, setRatingFilter] = useState('all');
  const navigate = useNavigate();

  useEffect(() => {
    if (!server) return;
    setLoading(true);
    const timeout = setTimeout(() => {
      api.get(`/api/admin/reports`)
        .then(res => setReports(res.data.reports || []))
        .catch(() => setReports([]))
        .finally(() => setLoading(false));
    });
    return () => clearTimeout(timeout);
  }, [server]);

  useEffect(() => {
    const ids = Array.from(new Set([
      ...reports.map(r => r.reporter),
      ...reports.map(r => r.admin)
    ].filter(Boolean)));
    if (ids.length === 0) return;
    ids.forEach(id => {
      if (!avatars[id]) {
        getCachedAvatar(id, async () => {
          const resp = await api.get(`/api/players/avatar/${id}`);
          return resp.data.avatar;
        }).then(url => {
          setAvatars(prev => ({ ...prev, [id]: url }));
        }).catch(() => {
          setAvatars(prev => ({ ...prev, [id]: null }));
        });
      }
      const steamId = steamId64ToV2(id);
      if (!names[steamId]) {
        getCachedDisplayName(id, async () => {
          try {
            const resp = await api.get(`/api/players/${steamId}`);
            return resp.data.displayName || resp.data.name || steamId;
          } catch (error) {
            if (error.response && error.response.status === 404) {
              return steamId;
            }
            throw error;
          }
        }).then(name => {
          setNames(prev => ({ ...prev, [steamId]: name }));
        }).catch(() => {
          setNames(prev => ({ ...prev, [steamId]: steamId }));
        });
      }
    });
  }, [reports]);

  useEffect(() => {
    setPage(1);
  }, [search, ratingFilter]);

  function getFilteredReports() {
    let filtered = reports;
    if (search.trim()) {
      const s = search.trim().toLowerCase();
      filtered = filtered.filter(r => {
        const reporterSteam = steamId64ToV2(r.reporter);
        const adminSteam = steamId64ToV2(r.admin);
        return (
          (names[reporterSteam] || reporterSteam || '').toLowerCase().includes(s) ||
          (names[adminSteam] || adminSteam || '').toLowerCase().includes(s) ||
          (reporterSteam || '').toLowerCase().includes(s) ||
          (adminSteam || '').toLowerCase().includes(s)
        );
      });
    }
    if (ratingFilter !== 'all') {
      filtered = filtered.filter(r => Math.round(r.rating) === Number(ratingFilter));
    }
    return filtered;
  }

  function getPageReports() {
    const filtered = getFilteredReports();
    const start = (page - 1) * PAGE_SIZE;
    return filtered.slice(start, start + PAGE_SIZE);
  }

  const totalPages = Math.ceil(getFilteredReports().length / PAGE_SIZE);

  return (
    <div className="bg-gmod-dark rounded-lg border border-gmod-lighterer p-6 mt-6">
      <h2 className="text-lg font-semibold text-gmod-white mb-4">Репорты</h2>
      <div className="flex flex-col md:flex-row gap-4 mb-4">
        <div className="flex-1">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gmod-white opacity-60" />
            <input
              type="text"
              placeholder="Поиск по имени или Steam ID..."
              value={search}
              onChange={e => setSearch(e.target.value)}
              className="w-full pl-10 pr-4 py-2 bg-gmod-lighter border border-gmod-lighterer rounded-md text-gmod-white placeholder-gmod-white placeholder-opacity-40 focus:outline-none focus:ring-2 focus:ring-theme-primary"
            />
          </div>
        </div>
        <div>
          <select
            value={ratingFilter}
            onChange={e => setRatingFilter(e.target.value)}
            className="px-4 py-2 bg-gmod-lighter border border-gmod-lighterer rounded-md text-gmod-white focus:outline-none focus:ring-2 focus:ring-theme-primary"
          >
            <option value="all">Все оценки</option>
            {[1,2,3,4,5].map(r => (
              <option key={r} value={r}>{r}★</option>
            ))}
          </select>
        </div>
      </div>
      {loading ? (
        <div className="text-gmod-white opacity-60">Загрузка...</div>
      ) : getFilteredReports().length === 0 ? (
        <div className="text-gmod-white opacity-60">Репортов нет</div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {getPageReports().map((c, i) => {
            const reporterSteam = steamId64ToV2(c.reporter);
            const adminSteam = steamId64ToV2(c.admin);
            return (
              <div key={i} className="bg-gmod-lighter rounded-lg p-4 border border-gmod-lighterer flex flex-col gap-2 hover:bg-gmod-dark transition-colors">
                <div className="flex items-center gap-3">
                  <span className="flex items-center cursor-pointer" onClick={() => navigate(`/players/${reporterSteam}`)}>
                    <User className="w-5 h-5 text-theme-primary mr-4" />
                    {avatars[c.reporter] ? (
                      <img src={avatars[c.reporter]} alt="avatar" className="w-12 h-12 min-w-[48px] rounded-full mr-2 align-middle -ml-2" />
                    ) : (
                      <img src="/default-avatar.png" alt="avatar" className="w-12 h-12 min-w-[48px] rounded-full mr-2 align-middle -ml-2" />
                    )}
                    <span className="text-theme-primary hover:underline align-middle font-medium">{names[reporterSteam] || reporterSteam}</span>
                    <span className="text-theme-primary opacity-60 ml-1 align-middle">({reporterSteam})</span>
                  </span>
                </div>
                <div className="flex items-center gap-3">
                  <span className="flex items-center cursor-pointer" onClick={() => navigate(`/players/${adminSteam}`)}>
                    <Shield className="w-5 h-5 text-gmod-blue mr-4" />
                    {avatars[c.admin] ? (
                      <img src={avatars[c.admin]} alt="avatar" className="w-12 h-12 min-w-[48px] rounded-full mr-2 align-middle -ml-2" />
                    ) : (
                      <img src="/default-avatar.png" alt="avatar" className="w-12 h-12 min-w-[48px] rounded-full mr-2 align-middle -ml-2" />
                    )}
                    <span className="hover:underline align-middle font-medium" style={{ color: '#778ea8' }}>{names[adminSteam] || adminSteam}</span>
                    <span className="opacity-60 ml-1 align-middle" style={{ color: '#778ea8' }}>({adminSteam})</span>
                  </span>
                </div>
                <div className="flex items-center gap-2 text-gmod-white opacity-60 text-sm">
                  <Clock className="h-4 w-4 mr-1 text-gmod-yellow" />
                  {c.date ? new Date(c.date * 1000).toLocaleString('ru-RU', { timeZone: 'Europe/Moscow' }) : '-'}
                </div>
                <div className="flex items-center gap-2 text-gmod-white opacity-60 text-sm">
                  <Clock className="h-4 w-4 mr-1 text-gmod-yellow" />
                  {c.waiting_time ? Math.round(c.waiting_time/60) + ' мин ожидания' : '-'}
                </div>
                <div className="flex items-center gap-2 text-gmod-white opacity-60 text-sm">
                  <Star className="h-4 w-4 mr-1 text-theme-primary" />
                  <span className="flex items-center">
                    {Array.from({ length: 5 }).map((_, i) => (
                      <svg key={i} width="18" height="18" viewBox="0 0 24 24" fill={i < Math.round(c.rating) ? '#dc255a' : 'none'} stroke="#dc255a" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="inline-block">
                        <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
                      </svg>
                    ))}
                  </span>
                </div>
                <div className="flex items-center gap-2 text-gmod-white opacity-60 text-sm">
                  <MessageCircle className="h-4 w-4 mr-1 text-gmod-green" />
                  {c.reason || '-'}
                </div>
                <div className="flex items-center gap-2 text-gmod-white opacity-60 text-sm">
                  <MessageCircle className="h-4 w-4 mr-1 text-gmod-green" />
                  {c.comment || '-'}
                </div>
              </div>
            );
          })}
        </div>
      )}
      {totalPages > 1 && (
        <div className="flex justify-center space-x-2 mt-4">
          <button onClick={() => setPage(Math.max(1, page - 1))} disabled={page === 1} className="px-4 py-2 bg-gmod-lighter text-gmod-white rounded-md disabled:opacity-50">Назад</button>
          <span className="px-4 py-2 text-gmod-white">{page} из {totalPages}</span>
          <button onClick={() => setPage(Math.min(totalPages, page + 1))} disabled={page === totalPages} className="px-4 py-2 bg-gmod-lighter text-gmod-white rounded-md disabled:opacity-50">Вперёд</button>
        </div>
      )}
    </div>
  );
} 